/**
 * Background-MUSIC volume authority — the ONE place the music level is defined.
 *
 * Owner feedback (2026-08-09): "sometimes the music gets really loud —
 * lesson→lesson, lesson→arcade, arcade→lesson, or when the device times out and
 * they log back into iOS it starts again a bit choppy or at a different volume.
 * There must be some drift in volume level. Also take the DEFAULT volume down a
 * bit for all music."
 *
 * Root causes of the drift were FOUR independent default literals (0.12 / 0.15 /
 * 0.18 across web-child, web-app and the two PreferencesPages — merely saving
 * preferences on one surface persisted ITS default and changed the music level
 * everywhere) plus an iOS gain-path collapse in MusicPlayer. This module kills
 * the first class: every player, provider and settings page imports these
 * constants; none may keep a local literal (source-guarded by
 * music-volume-authority.test.ts in web-child).
 *
 * The effective output level is ALWAYS:
 *     musicTargetGain(userVolume, muted, ducked)
 * where userVolume defaults to MUSIC_DEFAULT_VOLUME. Only MusicPlayer may write
 * the resulting value to a gain node / media element.
 */

/**
 * Global default music volume (linear gain on the WebAudio GainNode).
 * 2026-08-09: lowered 0.15 → 0.10 (≈ −3.5 dB) — owner: "take the DEFAULT volume
 * down a bit for all music". A stored user preference always wins over this.
 * 2026-08-12: lowered 0.10 → 0.06 (−40%, ≈ −4.4 dB) — owner: "for all games make
 * it a bit quieter — it's too loud".
 */
export const MUSIC_DEFAULT_VOLUME = 0.06;

/**
 * Duck to ~2/5 of the current volume while TTS speaks — audible under
 * near-continuous lesson narration (owner: "always keep music"), still clearly
 * speech-first. Unified across apps (web-app previously drifted at 0.25).
 */
export const MUSIC_DUCK_RATIO = 0.4;

export const MUSIC_DUCK_DOWN_MS = 400;
export const MUSIC_DUCK_UP_MS = 600;

/** Track/theme transitions must FADE, never hard-cut (lesson→arcade→lesson). */
export const MUSIC_FADE_OUT_MS = 300;
export const MUSIC_FADE_IN_MS = 450;

/**
 * Background-music playback rate — MUST be 1.0 (native, unaltered speed).
 *
 * History: 2026-08-12 this was set to 0.9 to calm the arcade music ("the music
 * is too fast, slow it down"), relying on preservesPitch so slowed tracks
 * wouldn't warp. But preservesPitch is NOT honored on every surface/WebView, so
 * the 0.9 rate leaked to ALL background music and rendered the parent-portal
 * landing music slow/garbled (owner 2026-08-15: "go back to the original music
 * we had last week"). Reverted to 1.0 — original quality everywhere. The
 * "too intense / too loud" concern is handled by MUSIC_DEFAULT_VOLUME (0.06),
 * NOT by a fragile global rate change. Do not re-slow music via playbackRate.
 * MUSIC ONLY — TTS/speech clips must never go through this.
 */
export const MUSIC_PLAYBACK_RATE = 1.0;

/**
 * Apply the shared music playback rate to a media element, enabling
 * pitch-preservation (incl. vendor-prefixed variants) where the platform
 * supports it. Defensive: jsdom/test mocks and old WebViews may lack any of
 * these properties — every write is best-effort and never throws.
 */
export function applyMusicPlaybackRate(audio: unknown): void {
  const el = audio as {
    playbackRate?: number;
    defaultPlaybackRate?: number;
    preservesPitch?: boolean;
    mozPreservesPitch?: boolean;
    webkitPreservesPitch?: boolean;
  };
  if (!el || typeof el !== 'object') return;
  // Pitch-preservation FIRST so the rate change is never audible as a warp.
  try { el.preservesPitch = true; } catch { /* unsupported — best effort */ }
  try { el.mozPreservesPitch = true; } catch { /* unsupported */ }
  try { el.webkitPreservesPitch = true; } catch { /* unsupported */ }
  try { el.defaultPlaybackRate = MUSIC_PLAYBACK_RATE; } catch { /* unsupported */ }
  try { el.playbackRate = MUSIC_PLAYBACK_RATE; } catch { /* unsupported */ }
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/** The one formula for the effective music output level. */
export function musicTargetGain(volume: number, muted: boolean, ducked: boolean): number {
  if (muted) return 0;
  const v = clamp01(volume);
  return ducked ? v * MUSIC_DUCK_RATIO : v;
}
